import { ComponentFactoryResolver, Directive, Input, OnChanges, SimpleChanges, TemplateRef, ViewContainerRef } from '@angular/core';
import * as get from 'lodash/get';

import { DefaultTemplateComponent } from '../component/default-template';
import { TabUtil } from '../utils';

/**
 * Conditionally show content based on the value of hidden flag and error flag.
 *
 *
 * ### Rules:
 *	hiddenFlag: hiddenFlag(tabConfig) or tableData is empty
 * - 1. no hiddenFlag && no errorFlag
 *   - show content(children dom)
 * - 2. hiddenFlag is true
 * 	 - 1) showMessage is true
 * 		- without hiddenView: show <p> with default dcr key(<NAME>_HIDDEN_MESSAGE)
 * 		- with hiddenView: show hiddenTemplate
 *   - 2) showMessage is false: show nothing
 * - 3. errorFlag is true
 * 	 - 1) showMessage is true
 * 		- without errorView: show <p> with default dcr key(<NAME>_ERROR_MESSAGE)
 * 		- with errorView: show errorTemplate
 *   - 2) showMessage is false: show nothing
 *
 *
 * ### Syntax:
 *
 * No Template, use dcr by default:
 * ```
 * <ng-template [displayData]="tabData">
 * </ng-template>
 * ```
 *
 * * No Template, hide content:
 * ```
 * <ng-template [displayTabData]="tabData" [showMessage]="false">
 * </ng-template>
 * ```
 *
 * With hiddenView and/or errorView:
 * ```
 * <ng-template [displayTabData]="tabData" [hiddenView]="hiddenTemplate" [errorView]="errorTemplate">
 * </ng-template>
 * <ng-template #hiddenTemplate>...</ng-template>
 * <ng-template #errorTemplate>...</ng-template>
 * ```
 *
 */

@Directive({
	// eslint-disable-next-line @angular-eslint/directive-selector
	selector: '[displayTabData]'
})
export class DisplayTabDataDirective implements OnChanges {
	// FIXME: displayData should have type of TabData, can't set it now otherwise mockData will fail in test.
	@Input('displayTabData') public displayTabData: any;
	@Input('hiddenView') private hiddenView?: TemplateRef<any>;
	@Input('errorView') private errorView?: TemplateRef<any>;
	@Input('showMessage') public showMessage?: boolean = true;

	constructor(private viewContainer: ViewContainerRef, private templateRef: TemplateRef<any>, private componentFactoryResolver: ComponentFactoryResolver) { }

	// NOTE: Please find the logic in the rules section above, update the rules when you are updating the code
	/**
	 * PLEASE DO NOT USE THIS DIRECTIVE FOR NOW, IT'S FOR FUTURE USE.
	 */
	public ngOnChanges(changes: SimpleChanges): void {
		// FIXME: attributes like hiddenFlag are mocked, update them when they are in place.
		const data = get(changes, 'displayTabData.currentValue');
		// NOTE: We'll want to move hiddenFlag, showMessage(maybe) to tab config and isEmptyTables to transform, we put them here temporarily
		const isEmptyTables = data.tableData.every(table => TabUtil.isEmptyTable(table));
		const hiddenFlag = data.hiddenFlag || isEmptyTables;
		if (data) {
			this.viewContainer.clear();
			if (!hiddenFlag && !data.errorFlag) {
				this.viewContainer.createEmbeddedView(this.templateRef);
			} else if (this.showMessage) {
				if (hiddenFlag) {
					if (this.hiddenView) {
						this.viewContainer.createEmbeddedView(this.hiddenView);
					} else {
						this.showComponent(data);
					}
				} else if (data.errorFlag) {
					if (this.errorView) {
						this.viewContainer.createEmbeddedView(this.errorView);
					} else {
						this.showComponent(data);
					}
				}
			}
		}
	}

	public showComponent(data: any): void {
		const defaultTemplateComponentResolver = this.componentFactoryResolver.resolveComponentFactory(DefaultTemplateComponent);
		const componentRef = this.viewContainer.createComponent(defaultTemplateComponentResolver, 0);
		componentRef.instance.dcrKey = data.dcrKey;
	}
}
